// Capped log of every outgoing broker/event-service command — backs the
// operator-facing Command Console drawer.
//
// OFF BY DEFAULT (OPUS — AE Drive slowdown). Operators turn it on from the
// footer Settings menu only when they need to capture a problem. When off,
// logCommand/updateCommandLog are a single boolean check and cost nothing.
//
// Why it had to change — the old version stored the caller's payload BY
// REFERENCE, capped only by entry COUNT (100), and re-read + re-wrote the
// entire log to localStorage on every call:
//
//   * eventService.trigger() for clock events passes the whole homeTeam and
//     awayTeam objects, each carrying its full .player squad and .lineup
//     arrays — ~36 KB per entry. 100 of those is ~3.6 MB of JSON, which is
//     ~7 MB in localStorage's UTF-16 encoding: the ~5 MB origin quota blows
//     after roughly 70 clock events, i.e. inside a single match. That is the
//     "size exceeded" error, and because the quota is per-ORIGIN it also
//     starts failing the session writes in sessionManager, not just this log.
//   * Holding those payloads by reference also pinned ~3.6 MB of live squad
//     objects in the heap that the app had otherwise finished with.
//   * Every command did readLog (parse whole log) + writeLog (stringify whole
//     log) TWICE — once in logCommand, once in updateCommandLog — all
//     synchronous on the main thread.
//
// So now: memory is authoritative, payloads are sanitised into detached
// copies at capture, the log is bounded in BYTES as well as entries, and
// persistence is debounced and degrades to memory-only if the quota is hit.
const STORAGE_KEY = 'aedrive_command_log';
const ENABLED_KEY = 'aedrive_command_log_enabled';

const MAX_ENTRIES = 100;
const MAX_TOTAL_CHARS = 192 * 1024; // whole-log budget, well under any quota
const MAX_ENTRY_CHARS = 8 * 1024; // one entry's serialised payload
const MAX_ARRAY_ITEMS = 3; // squad/lineup arrays collapse to this
const MAX_STRING = 256;
const PERSIST_DEBOUNCE_MS = 750;

let listeners = [];
let enabledListeners = [];
let log = []; // authoritative, in memory
let enabled = false;
let persistTimer = null;
let persistBroken = false; // set once the quota rejects us — memory-only after

/**
 * Detach + shrink a payload so an entry can never hold a live app object or
 * an unbounded blob. Long arrays (squads, lineups, timelines) collapse to
 * MAX_ARRAY_ITEMS with a count marker; long strings truncate. Scalars at
 * shallow depth survive untouched, so the drawer's summarize() still finds
 * caption.action / layer / type / event_op.
 */
const sanitize = (payload) => {
  if (payload == null || typeof payload !== 'object') return payload;
  const seen = new WeakSet();
  let json;
  try {
    json = JSON.stringify(payload, (key, val) => {
      if (typeof val === 'function') return '[Function]';
      if (typeof val === 'bigint') return `${val}n`;
      if (typeof val === 'string' && val.length > MAX_STRING) {
        return `${val.slice(0, MAX_STRING)}…(+${val.length - MAX_STRING} chars)`;
      }
      if (Array.isArray(val) && val.length > MAX_ARRAY_ITEMS) {
        return [
          ...val.slice(0, MAX_ARRAY_ITEMS),
          `…(+${val.length - MAX_ARRAY_ITEMS} more items elided)`,
        ];
      }
      if (val && typeof val === 'object') {
        if (val.nodeType) return '[DOM node]';
        if (seen.has(val)) return '[Circular]';
        seen.add(val);
      }
      return val;
    });
  } catch (e) {
    return { _unserializable: String(e?.message || e) };
  }
  if (json == null) return null;
  if (json.length > MAX_ENTRY_CHARS) {
    // Still oversized after eliding. Keep only what the drawer summarises on
    // plus a head slice, so one pathological command can't dominate the log.
    const head = (o, keys) =>
      keys.reduce((a, k) => (o?.[k] !== undefined ? { ...a, [k]: o[k] } : a), {});
    return {
      ...head(payload, ['renderer', 'layer', 'type', 'event_op', 'goal_type']),
      caption: head(payload?.caption, ['id', 'action', 'mode']),
      _elided: `payload ${json.length.toLocaleString()} chars — too large to keep`,
    };
  }
  // Parse back so the drawer gets a plain object, fully detached from the app.
  try {
    return JSON.parse(json);
  } catch (e) {
    return { _unserializable: 'reparse failed' };
  }
};

const entryCost = (entry) => {
  try {
    return JSON.stringify(entry).length;
  } catch (e) {
    return MAX_ENTRY_CHARS;
  }
};

/** Trim oldest-first until both the entry count and byte budget are satisfied. */
const trim = () => {
  if (log.length > MAX_ENTRIES) log.length = MAX_ENTRIES;
  let total = log.reduce((a, e) => a + (e._cost || 0), 0);
  while (log.length > 1 && total > MAX_TOTAL_CHARS) {
    total -= log[log.length - 1]._cost || 0;
    log.pop();
  }
};

const persistNow = () => {
  if (persistBroken) return;
  try {
    // _cost is bookkeeping — strip it from what we store.
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(log.map(({ _cost, ...e }) => e)),
    );
  } catch (e) {
    // Almost always QuotaExceededError. Halve and retry once; if that still
    // fails, stop persisting for the session rather than throwing on every
    // command. The in-memory log keeps working, so the drawer is unaffected.
    try {
      log.length = Math.floor(log.length / 2);
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(log.map(({ _cost, ...e }) => e)),
      );
    } catch (e2) {
      persistBroken = true;
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e3) {
        /* nothing left to try */
      }
      console.warn(
        '[commandLog] storage quota exceeded — continuing in memory only for this session',
      );
    }
  }
};

const schedulePersist = () => {
  if (persistBroken || persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    persistNow();
  }, PERSIST_DEBOUNCE_MS);
};

const notify = () => {
  const view = getCommandLog();
  listeners.forEach((cb) => {
    try {
      cb(view);
    } catch (e) {
      /* a broken listener must not break logging */
    }
  });
};

// --- enable / disable -------------------------------------------------------

export const isCommandLogEnabled = () => enabled;

export const setCommandLogEnabled = (on) => {
  const next = !!on;
  if (next === enabled) return;
  enabled = next;
  try {
    localStorage.setItem(ENABLED_KEY, String(next));
  } catch (e) {
    /* preference is best-effort */
  }
  if (!enabled) {
    // Turning it off frees everything it was holding — that is the point.
    log = [];
    persistBroken = false;
    if (persistTimer) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
    notify();
  }
  enabledListeners.forEach((cb) => {
    try {
      cb(enabled);
    } catch (e) {
      /* ignore */
    }
  });
};

export const subscribeCommandLogEnabled = (cb) => {
  enabledListeners.push(cb);
  return () => {
    enabledListeners = enabledListeners.filter((l) => l !== cb);
  };
};

// --- log --------------------------------------------------------------------

export const getCommandLog = () => log.map(({ _cost, ...e }) => e);

// source: 'broker' | 'event-service'. Returns the stored entry (with id) so
// callers can patch it later. When disabled, returns a sentinel with a null
// id — updateCommandLog ignores it, so call sites need no changes.
const DISABLED_ENTRY = Object.freeze({ id: null });

export const logCommand = (entry) => {
  if (!enabled) return DISABLED_ENTRY;
  const withMeta = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
    ...entry,
    payload: sanitize(entry?.payload),
  };
  withMeta._cost = entryCost(withMeta);
  log.unshift(withMeta);
  trim();
  notify();
  schedulePersist();
  const { _cost, ...pub } = withMeta;
  return pub;
};

export const updateCommandLog = (id, patch) => {
  if (!enabled || !id) return;
  const i = log.findIndex((e) => e.id === id);
  if (i === -1) return;
  const next = { ...log[i], ...patch };
  next._cost = entryCost(next);
  log[i] = next;
  trim();
  notify();
  schedulePersist();
};

export const clearCommandLog = () => {
  log = [];
  persistBroken = false;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
  notify();
};

// Returns an unsubscribe function.
export const subscribeCommandLog = (cb) => {
  listeners.push(cb);
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
};

// --- hydrate ----------------------------------------------------------------

(() => {
  try {
    enabled = localStorage.getItem(ENABLED_KEY) === 'true';
  } catch (e) {
    enabled = false;
  }
  if (!enabled) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) {
      log = parsed.map((e) => ({ ...e, _cost: entryCost(e) }));
      trim();
    }
  } catch (e) {
    console.warn('[commandLog] failed to restore log', e);
    log = [];
  }
})();
